/**
 * Copy Trading Portfolio Store (Zustand)
 *
 * 카피 트레이딩 포트폴리오 및 팔로우 상태 관리
 * - persist 미들웨어로 localStorage에 저장
 * - API 호출 및 상태 업데이트
 * - 계산된 값 제공 (총 손익, 총 할당 금액 등)
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { message } from 'antd';
import { getBigNumber } from '../../../utils/getBigNumber';
import i18next from 'i18next';

import type {
  CopyPortfolio,
  CopyPortfolioStats,
  CopyFollow,
  GetPortfoliosParams,
  FollowPortfolioParams,
  UpdateFollowParams,
} from '../../../types/copyTrading';

import {
  getPortfolios,
  getPortfolioById,
  followPortfolio,
  pauseFollow,
  resumeFollow,
  updateFollow,
  unfollowPortfolio,
  getMyFollows,
} from '../../api/copyTrading';

/**
 * Store State 인터페이스
 */
interface CopyPortfolioState {
  // ==========================================
  // State
  // ==========================================

  /** 포트폴리오 목록 */
  portfolios: CopyPortfolio[];

  /** 포트폴리오 통계 맵 (portfolioId → stats) */
  portfolioStatsMap: Record<number, CopyPortfolioStats>;

  /** 내 팔로우 목록 */
  myFollows: Array<{
    follow: CopyFollow;
    portfolio: CopyPortfolio;
    stats: CopyPortfolioStats;
  }>;

  /** 선택된 포트폴리오 (상세보기) */
  selectedPortfolio: CopyPortfolio | null;

  /** 요약 정보 */
  summary: {
    totalAllocated: number;
    totalPnl: number;
    totalProfitShared: number;
  };

  /** 로딩 상태 */
  isLoading: boolean;

  /** 에러 메시지 */
  error: string | null;

  // ==========================================
  // Actions
  // ==========================================

  /** 포트폴리오 목록 조회 */
  fetchPortfolios: (params?: GetPortfoliosParams) => Promise<void>;

  /** 포트폴리오 상세 정보 조회 */
  fetchPortfolioById: (id: number) => Promise<void>;

  /** 포트폴리오 팔로우 시작 */
  startFollow: (params: FollowPortfolioParams) => Promise<void>;

  /** 팔로우 일시 중지 */
  pauseFollow: (followId: number) => Promise<void>;

  /** 팔로우 재개 */
  resumeFollow: (followId: number) => Promise<void>;

  /** 팔로우 설정 수정 */
  updateFollowSettings: (params: UpdateFollowParams) => Promise<void>;

  /** 팔로우 종료 */
  stopFollow: (followId: number) => Promise<void>;

  /** 내 팔로우 목록 조회 */
  fetchMyFollows: () => Promise<void>;

  /** 선택된 포트폴리오 설정 */
  setSelectedPortfolio: (portfolio: CopyPortfolio | null) => void;

  /** 에러 초기화 */
  clearError: () => void;

  // ==========================================
  // Computed Values
  // ==========================================

  /** 총 손익 계산 */
  getTotalPnl: () => number;

  /** 총 할당 금액 계산 */
  getTotalAllocated: () => number;

  /** ROI 계산 */
  getTotalRoi: () => number;
}

/**
 * Copy Portfolio Store
 */
export const useCopyPortfolioStore = create<CopyPortfolioState>()(
  persist(
    (set, get) => ({
      // ==========================================
      // Initial State
      // ==========================================
      portfolios: [],
      portfolioStatsMap: {},
      myFollows: [],
      selectedPortfolio: null,
      summary: {
        totalAllocated: 0,
        totalPnl: 0,
        totalProfitShared: 0,
      },
      isLoading: false,
      error: null,

      // ==========================================
      // Actions
      // ==========================================

      /**
       * 포트폴리오 목록 조회
       */
      fetchPortfolios: async (params = {}) => {
        set({ isLoading: true, error: null });

        try {
          const response = await getPortfolios(params);

          if (response.success) {
            set({
              portfolios: response.data?.data || [],
              isLoading: false,
            });
          } else {
            set({
              error: response.error?.message || i18next.t('getPortfolioFailed'),
              isLoading: false,
              portfolios: [], // 빈 배열로 초기화
            });
          }
        } catch (error: any) {
          console.error('[Copy Portfolio Store] fetchPortfolios error:', error);

          const errorMsg =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            error.message ||
            i18next.t('getPortfolioFailed');

          set({
            error: errorMsg,
            isLoading: false,
            portfolios: [], // 에러 시에도 빈 배열
          });
          // message.error(errorMsg);
        }
      },

      /**
       * 포트폴리오 상세 정보 조회
       */
      fetchPortfolioById: async (id: number) => {
        set({ isLoading: true, error: null });

        try {
          const response = await getPortfolioById(id);
          console.log('[Portfolio Detail] API Response:', response);

          if (response.success) {
            // Real API는 response.data가 바로 portfolio 객체
            const portfolio = response.data;

            set({
              selectedPortfolio: portfolio,
              portfolioStatsMap: {
                ...get().portfolioStatsMap,
                [id]: {} as any, // Mock stats (추후 구현)
              },
              isLoading: false,
            });
          } else {
            const errorMsg =
              response.error?.message || i18next.t('getPortfolioFailed');
            set({
              error: errorMsg,
              isLoading: false,
              selectedPortfolio: null,
            });
            // message.error(errorMsg);
          }
        } catch (error: any) {
          console.error(
            '[Copy Portfolio Store] fetchPortfolioById error:',
            error
          );

          const errorMsg =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            error.message ||
            i18next.t('getPortfolioFailed');

          set({
            error: errorMsg,
            isLoading: false,
            selectedPortfolio: null,
          });
          // message.error(errorMsg);
        }
      },

      /**
       * 포트폴리오 팔로우 시작
       */
      startFollow: async (params: FollowPortfolioParams) => {
        set({ isLoading: true, error: null });

        try {
          const response = await followPortfolio(params);

          if (response.success) {
            // 팔로우 성공 후 내 팔로우 목록 갱신
            await get().fetchMyFollows();

            set({ isLoading: false });
            // message.success(i18next.t('followStartSuccess'));
          } else {
            const errorMsg =
              response.error?.message || i18next.t('followStartFailed');
            set({
              error: errorMsg,
              isLoading: false,
            });
            // message.error(errorMsg);
            throw new Error(errorMsg);
          }
        } catch (error: any) {
          console.error('[Copy Portfolio Store] startFollow error:', error);

          // Axios 에러 응답 파싱
          const errorMsg =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            error.message ||
            i18next.t('followStartFailed');

          set({
            // error: errorMsg,
            isLoading: false,
          });
          // message.error(errorMsg);
          throw error;
        }
      },

      /**
       * 팔로우 일시 중지
       */
      pauseFollow: async (followId: number) => {
        set({ isLoading: true, error: null });

        try {
          const response = await pauseFollow(followId);

          if (response.success) {
            // 팔로우 목록에서 해당 팔로우 상태 업데이트
            const myFollows = get().myFollows.map((item) =>
              item.follow.id === followId
                ? { ...item, follow: response.data }
                : item
            );

            set({ myFollows, isLoading: false });
          } else {
            set({
              error: response.error?.message || i18next.t('followPauseFailed'),
              isLoading: false,
            });
            throw new Error(
              response.error?.message || i18next.t('followPauseFailed')
            );
          }
        } catch (error: any) {
          console.error('[Copy Portfolio Store] pauseFollow error:', error);
          set({
            error: error.message || i18next.t('followPauseFailed'),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * 팔로우 재개
       */
      resumeFollow: async (followId: number) => {
        set({ isLoading: true, error: null });

        try {
          const response = await resumeFollow(followId);

          if (response.success) {
            // 팔로우 목록에서 해당 팔로우 상태 업데이트
            const myFollows = get().myFollows.map((item) =>
              item.follow.id === followId
                ? { ...item, follow: response.data }
                : item
            );

            set({ myFollows, isLoading: false });
          } else {
            set({
              error: response.error?.message || i18next.t('followResumeFailed'),
              isLoading: false,
            });
            throw new Error(
              response.error?.message || i18next.t('followResumeFailed')
            );
          }
        } catch (error: any) {
          console.error('[Copy Portfolio Store] resumeFollow error:', error);
          set({
            error: error.message || i18next.t('followResumeFailed'),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * 팔로우 설정 수정
       */
      updateFollowSettings: async (params: UpdateFollowParams) => {
        set({ isLoading: true, error: null });

        try {
          console.log(
            '[Copy Portfolio Store] updateFollowSettings called with:',
            params
          );
          const response = await updateFollow(params);
          console.log(
            '[Copy Portfolio Store] updateFollowSettings response:',
            response
          );

          if (response.success) {
            // 응답 데이터 검증
            if (!response.data || !response.data.copyRatio) {
              console.error(
                '[Copy Portfolio Store] Invalid response data:',
                response.data
              );
              throw new Error(i18next.t('invalidResponseData'));
            }

            // 팔로우 목록에서 해당 팔로우 설정 업데이트
            const myFollows = get().myFollows.map((item) => {
              if (item.follow.id === params.followId) {
                console.log('[Copy Portfolio Store] Updating follow:', {
                  old: item.follow,
                  new: response.data,
                });
                return { ...item, follow: response.data };
              }
              return item;
            });

            console.log(
              '[Copy Portfolio Store] Updated myFollows count:',
              myFollows.length
            );
            set({ myFollows, isLoading: false });
          } else {
            set({
              error: response.error?.message || i18next.t('followUpdateFailed'),
              isLoading: false,
            });
            throw new Error(
              response.error?.message || i18next.t('followUpdateFailed')
            );
          }
        } catch (error: any) {
          console.error(
            '[Copy Portfolio Store] updateFollowSettings error:',
            error
          );
          set({
            error: error.message || i18next.t('followUpdateFailed'),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * 팔로우 종료
       */
      stopFollow: async (followId: number) => {
        set({ isLoading: true, error: null });

        try {
          const response = await unfollowPortfolio(followId);

          if (response.success) {
            // 팔로우 목록에서 제거 (또는 상태 업데이트)
            const myFollows = get().myFollows.filter(
              (item) => item.follow.id !== followId
            );

            set({ myFollows, isLoading: false });
          } else {
            set({
              error: response.error?.message || i18next.t('followStopFailed'),
              isLoading: false,
            });
            throw new Error(
              response.error?.message || i18next.t('followStopFailed')
            );
          }
        } catch (error: any) {
          console.error('[Copy Portfolio Store] stopFollow error:', error);
          set({
            error: error.message || i18next.t('followStopFailed'),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * 내 팔로우 목록 조회
       */
      fetchMyFollows: async () => {
        set({ isLoading: true, error: null });

        try {
          const response = await getMyFollows();
          console.log('[fetchMyFollows] API Response:', response);

          if (response.success) {
            // Real API: response.data.follows가 follow 배열
            const followItems = response.data?.follows || [];

            console.log(
              '[fetchMyFollows] Follow items count:',
              followItems.length
            );
            console.log('[fetchMyFollows] First item:', followItems[0]);

            // myFollows 구조로 변환 (API 응답이 이미 올바른 구조)
            const myFollows = followItems.map((item) => {
              console.log('[fetchMyFollows] Mapping item:', item);

              return {
                follow: item.follow,
                portfolio: item.portfolio || null,
                stats: item.stats || ({} as any),
              };
            });

            console.log('[fetchMyFollows] Mapped result:', myFollows);

            // summary 계산 (Real API에는 없으므로 직접 계산)
            const totalAllocated = followItems.reduce(
              (sum, item) =>
                sum +
                (typeof (item as any).follow?.allocatedAmount === 'number'
                  ? (item as any).follow.allocatedAmount
                  : parseFloat((item as any).follow?.allocatedAmount || '0')),
              0
            );
            const totalPnl = followItems.reduce(
              (sum, item) =>
                sum +
                (typeof (item as any).follow?.currentPnl === 'number'
                  ? (item as any).follow.currentPnl
                  : parseFloat((item as any).follow?.currentPnl || '0')),
              0
            );

            console.log(
              '[fetchMyFollows] Setting state - myFollows length:',
              myFollows.length
            );

            set({
              myFollows,
              summary: {
                totalAllocated,
                totalPnl,
                totalProfitShared: 0, // MVP에서는 0
              },
              isLoading: false,
            });

            console.log('[fetchMyFollows] State set complete');
          } else {
            const errorMsg =
              response.error?.message || i18next.t('fetchMyFollowsFailed');
            set({
              error: errorMsg,
              isLoading: false,
              myFollows: [],
            });
            // message.error(errorMsg);
          }
        } catch (error: any) {
          console.error('[Copy Portfolio Store] fetchMyFollows error:', error);

          const errorMsg =
            error.response?.data?.error?.message ||
            error.message ||
            i18next.t('fetchMyFollowsFailed');

          set({
            error: errorMsg,
            isLoading: false,
            myFollows: [],
          });
          // message.error(errorMsg);
        }
      },

      /**
       * 선택된 포트폴리오 설정
       */
      setSelectedPortfolio: (portfolio: CopyPortfolio | null) => {
        set({ selectedPortfolio: portfolio });
      },

      /**
       * 에러 초기화
       */
      clearError: () => {
        set({ error: null });
      },

      // ==========================================
      // Computed Values
      // ==========================================

      /**
       * 총 손익 계산
       */
      getTotalPnl: () => {
        const { myFollows } = get();
        return myFollows.reduce(
          (sum, item) =>
            getBigNumber(sum).plus(item.follow.currentPnl).toNumber(),
          0
        );
      },

      /**
       * 총 할당 금액 계산
       */
      getTotalAllocated: () => {
        const { myFollows } = get();
        return myFollows.reduce(
          (sum, item) =>
            getBigNumber(sum).plus(item.follow.allocatedAmount).toNumber(),
          0
        );
      },

      /**
       * ROI 계산 (%)
       */
      getTotalRoi: () => {
        const totalPnl = get().getTotalPnl();
        const totalAllocated = get().getTotalAllocated();

        if (totalAllocated === 0) return 0;

        return getBigNumber(totalPnl)
          .dividedBy(totalAllocated)
          .multipliedBy(100)
          .toNumber();
      },
    }),
    {
      name: 'copy-portfolio-store', // localStorage 키
      // 일부 상태만 persist (로딩/에러는 제외)
      partialize: (state) => ({
        portfolios: state.portfolios,
        myFollows: state.myFollows,
        selectedPortfolio: state.selectedPortfolio,
        summary: state.summary,
      }),
    }
  )
);
